/**
 * 🚨 Issue Types — MASP (Método de Análise e Solução de Problemas)
 */

export type IssueType =
  | 'incident'
  | 'non_conformity'
  | 'deviation'
  | 'customer_complaint'
  | 'operational_failure';

export type IssueSeverity = 'critical' | 'high' | 'medium' | 'low';

export type IssueMode = 'quick_correction' | 'structured_problem';

export type IssueStatus =
  | 'open'
  | 'investigation'
  | 'root_cause'
  | 'action_plan'
  | 'execution'
  | 'validation'
  | 'monitoring'
  | 'closed';

export type RootCauseCategory =
  | 'method'
  | 'machine'
  | 'material'
  | 'manpower'
  | 'environment'
  | 'measurement';

/** 5 Whys entry */
export interface FiveWhyEntry {
  level: number; // 1-5
  question: string;
  answer: string;
}

/** Estado da hipótese no 6M. Não é a confirmação da causa do problema. */
export type IshikawaHypothesisStatus = 'candidate' | 'investigating' | 'discarded' | 'confirmed';

export type HypothesisInvestigationMethod = 'five_whys' | 'direct';

/** Resposta do 5 Porquês de uma hipótese. O último item não confirma a causa. */
export interface HypothesisWhy {
  id: string;
  answer: string;
}

/** Investigação pertencente ao id da hipótese. */
export interface HypothesisInvestigation {
  method: HypothesisInvestigationMethod;
  whys?: HypothesisWhy[];
  whys_closed?: boolean;
  conclusion?: string;
  evidence?: string;
  discard_reason?: string;
}

/** Hipótese de um ramo 6M. Registros antigos ainda podem chegar como string. */
export interface IshikawaHypothesis {
  id: string;
  text: string;
  category: RootCauseCategory;
  status: IshikawaHypothesisStatus;
  probable: boolean;
  investigation?: HypothesisInvestigation;
}

/** Ishikawa cause. `causes` aceita string legada ou hipótese estruturada. */
export interface IshikawaCause {
  category: RootCauseCategory;
  causes: Array<string | IshikawaHypothesis>;
}

export type CauseAnalysisMethod = 'five_whys' | 'ishikawa' | 'free' | 'other';

export interface SuggestedHypothesis {
  text: string;
  source: 'ai' | 'free';
}

/** Root Cause Analysis data. Hipóteses ficam em 5 Porquês, Ishikawa ou sugestões. Causa só existe com confirmação humana. */
export interface RootCauseAnalysis {
  problem_statement: string;
  root_cause_description: string;
  root_cause_category: RootCauseCategory;
  five_whys?: FiveWhyEntry[];
  ishikawa?: IshikawaCause[];
  suggested_hypotheses?: SuggestedHypothesis[];
  cause_confirmed?: boolean;
  cause_evidence?: string;
  analysis_method?: CauseAnalysisMethod;
}

// ─── Validation & Monitoring Types ────────────────────────────

export type ValidationMode = 'time' | 'volume' | 'indicator' | 'manual';
export type MonitoringMode = 'manual' | 'indicator';

export interface TimeValidation {
  validation_days: number;
}

export interface VolumeValidation {
  validation_units: number;
}

export interface IndicatorValidation {
  indicator_id: string;
  target_value: number;
  validation_period: number; // days
}

export interface ManualInspection {
  inspection_date: string;
  notes: string;
  inspector_id?: string;
}

export interface ValidationConfig {
  mode: ValidationMode;
  time?: TimeValidation;
  volume?: VolumeValidation;
  indicator?: IndicatorValidation;
  inspections?: ManualInspection[];
  started_at?: string;
  completed_at?: string;
  result?: 'passed' | 'failed' | null;
}

export interface MonitoringConfig {
  mode: MonitoringMode;
  indicator_id?: string;
  threshold_value?: number;
  started_at?: string;
  last_check_at?: string;
  checks_count?: number;
}

// ─── Recurrence & Alerts ──────────────────────────────────────

export type AlertType = 'recurrence' | 'monitoring_failure' | 'indicator_deviation';

export interface ProblemAlert {
  id: string;
  type: AlertType;
  message: string;
  related_issue_id?: string;
  indicator_id?: string;
  created_at: string;
  dismissed?: boolean;
}

export interface RecurrenceMatch {
  issue_id: string;
  issue_title: string;
  similarity_score: number;
  matching_fields: string[];
  created_at: string;
}

export interface IssueHistoryEntry {
  id: string;
  type: string;
  message: string;
  actor_id?: string;
  actor_name?: string;
  at: string;
  meta?: Record<string, unknown>;
}

export interface IssueMetadata {
  history?: IssueHistoryEntry[];
  recurrence_detected?: boolean;
  force_structured?: boolean;
  linked_hoshin_goal_id?: string;
  verification_owner_id?: string;
  closure_reason?: string;
  verification_due_at?: string;
  [key: string]: unknown;
}

export interface Issue {
  id: string;
  organization_id: string;
  title: string;
  description?: string | null;
  type: IssueType;
  severity: IssueSeverity;
  status: IssueStatus;
  mode: IssueMode;
  root_cause?: string | null;
  root_cause_analysis?: RootCauseAnalysis | null;
  validation_config?: ValidationConfig | null;
  monitoring_config?: MonitoringConfig | null;
  resolution?: string | null;
  department?: string | null;
  process?: string | null;
  step?: string | null;
  created_by?: string | null;
  assigned_to?: string | null;
  project_id?: string | null;
  visibility_scope?: 'private' | 'project' | 'organization';
  owner_id?: string | null;
  workflow_id?: string | null;
  action_id?: string | null;
  linked_actions_count?: number;
  /** Derivado na UI. Não é coluna. */
  efficacy_ready?: boolean;
  source?: string | null;
  tags?: string[];
  metadata?: IssueMetadata | null;
  plan_id?: string | null;
  code?: string | null;
  created_at: string;
  updated_at: string;
  resolved_at?: string | null;
  due_date?: string | null;
}

export const ISSUE_TYPE_LABELS: Record<IssueType, string> = {
  incident: 'Incidente',
  non_conformity: 'Não Conformidade',
  deviation: 'Desvio',
  customer_complaint: 'Reclamação de Cliente',
  operational_failure: 'Falha Operacional',
};

export const ISSUE_SEVERITY_LABELS: Record<IssueSeverity, string> = {
  critical: 'Crítica',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

export const ISSUE_STATUS_LABELS: Record<IssueStatus, string> = {
  open: 'Aberto',
  investigation: 'Investigação',
  root_cause: 'Causa Raiz',
  action_plan: 'Plano de Ação',
  execution: 'Execução',
  validation: 'Validação',
  monitoring: 'Monitoramento',
  closed: 'Encerrado',
};

export const ISSUE_MODE_LABELS: Record<IssueMode, string> = {
  quick_correction: 'Correção Rápida',
  structured_problem: 'Problema Estruturado',
};

export const ROOT_CAUSE_CATEGORY_LABELS: Record<RootCauseCategory, string> = {
  method: 'Método',
  machine: 'Máquina',
  material: 'Material',
  manpower: 'Mão de Obra',
  environment: 'Meio Ambiente',
  measurement: 'Medição',
};

export const VALIDATION_MODE_LABELS: Record<ValidationMode, string> = {
  time: 'Tempo',
  volume: 'Volume',
  indicator: 'Indicador',
  manual: 'Manual',
};

export const MONITORING_MODE_LABELS: Record<MonitoringMode, string> = {
  manual: 'Manual',
  indicator: 'Indicador',
};

export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  recurrence: 'Possível Recorrência',
  monitoring_failure: 'Falha de Monitoramento',
  indicator_deviation: 'Desvio de Indicador',
};

/** Transições válidas de status para MASP (Structured Problem) */
export const ISSUE_STATUS_TRANSITIONS: Record<IssueStatus, IssueStatus[]> = {
  open: ['investigation', 'closed'],
  investigation: ['root_cause', 'action_plan', 'closed'],
  root_cause: ['action_plan', 'investigation'],
  action_plan: ['execution', 'root_cause'],
  execution: ['validation', 'action_plan'],
  validation: ['monitoring', 'closed', 'execution'],
  monitoring: ['closed', 'validation'],
  closed: ['open'],
};

/** Transições válidas de status para Correção Rápida (Quick Correction) */
export const ISSUE_STATUS_TRANSITIONS_QUICK: Record<IssueStatus, IssueStatus[]> = {
  open: ['action_plan', 'closed'],
  investigation: [],
  root_cause: [],
  action_plan: ['execution', 'closed'],
  execution: ['validation'],
  validation: ['execution', 'closed'],
  monitoring: [],
  closed: ['open'],
};

export const MASP_STAGE_ORDER: IssueStatus[] = [
  'open', 'investigation', 'root_cause', 'action_plan',
  'execution', 'validation', 'monitoring', 'closed',
];

export const QUICK_STAGE_ORDER: IssueStatus[] = [
  'open', 'action_plan', 'execution', 'validation', 'closed',
];

export const getStageOrder = (mode: IssueMode): IssueStatus[] => {
  return mode === 'quick_correction' ? QUICK_STAGE_ORDER : MASP_STAGE_ORDER;
};

export const getStatusTransitions = (mode: IssueMode): Record<IssueStatus, IssueStatus[]> => {
  return mode === 'quick_correction' ? ISSUE_STATUS_TRANSITIONS_QUICK : ISSUE_STATUS_TRANSITIONS;
};

/** Status fora do roteiro do modo continua legível. A transição de recuperação usa `open`. */
export const effectiveStatusForMode = (mode: IssueMode, status: IssueStatus): IssueStatus => {
  return getStageOrder(mode).includes(status) ? status : 'open';
};

export const isStatusOnModePath = (mode: IssueMode, status: IssueStatus): boolean => {
  return getStageOrder(mode).includes(status);
};

export const isTransitionAllowed = (mode: IssueMode, from: IssueStatus, to: IssueStatus): boolean => {
  const origin = effectiveStatusForMode(mode, from);
  return (getStatusTransitions(mode)[origin] ?? []).includes(to);
};

export const transitionRefusal = (mode: IssueMode, from: IssueStatus, to: IssueStatus): string => {
  if (isTransitionAllowed(mode, from, to)) return '';
  const fromLabel = ISSUE_STATUS_LABELS[from] ?? from;
  const toLabel = ISSUE_STATUS_LABELS[to] ?? to;
  if (to === 'closed' && (from === 'execution' || from === 'action_plan' || from === 'investigation' || from === 'root_cause')) {
    return 'O encerramento vem depois da verificação. Esta etapa ainda não encerra o problema.';
  }
  if (mode === 'quick_correction' && (to === 'investigation' || to === 'root_cause' || to === 'monitoring')) {
    return 'Correção rápida não usa esta etapa. O caminho é plano, execução, verificação e encerramento.';
  }
  if (!isStatusOnModePath(mode, from)) {
    return `Fluxo legado. O registro está em ${fromLabel} e não pode ir para ${toLabel} por este caminho.`;
  }
  return `Não é possível ir de ${fromLabel} para ${toLabel} neste tratamento.`;
};
